<template>
  <div class="ti-stepper" v-if="step.stepId">
    <ConfirmationCe
      :step="step"
      :history="history"
      :loading="loading"
      v-if="
        step.stepId === 'emaildealerconfirmation' ||
        step.stepId === 'dealerconfirmation'
      "
    ></ConfirmationCe>

    <template
      v-if="
        step.stepId !== 'emaildealerconfirmation' &&
        step.stepId !== 'dealerconfirmation'
      "
      v-for="(item, index) in step.stepList"
    >
      <StepCe
        :step="item"
        :title="
          checkIfActive(item.stepId.toLowerCase()) &&
          item.stepId.toLowerCase() === 'emailconfirmation'
            ? ''
            : item.headline
        "
        :index="index + 1"
        :id="item.stepId.toLowerCase()"
        :completed="checkIfCompleted(item.stepId.toLowerCase())"
        :active="checkIfActive(item.stepId.toLowerCase())"
        :expander="
          item.stepId.toLowerCase() === 'emailconfirmation' &&
          step.data.residualValue
            ? false
            : true
        "
        @navigateToStep="navigateToStep"
      >
        <template
          v-slot:copy
          v-if="
            item.stepId.toLowerCase() === 'dealerselection' &&
            step.data.dealerInfo &&
            !checkIfActive(item.stepId.toLowerCase())
          "
        >
          {{ step.data.dealerInfo?.address.line1 }},
          {{ step.data.dealerInfo?.address.zipcode }}
          {{ step.data.dealerInfo?.address.city }}
        </template>

        <template
          v-slot:copy
          v-if="
            item.stepId.toLowerCase() === 'emailconfirmation' &&
            step.data.residualValue &&
            !checkIfActive(item.stepId.toLowerCase())
          "
        >
          <span>{{ step.data.residualValue }}</span>
          <ModalComponentCe
            :modal="$t('i18n.summary.residualValue.modal') as any"
          />
        </template>

        <component
          class="ti__content"
          :is="currentComponent"
          :key="currentComponentName"
          :step="step"
          :loading="loading"
          :updating="updating"
          :history="history"
          :notification="notification"
          @closeNotification="closeNotification"
          @navigateToStep="navigateToStep"
          @stepChanged="stepChanged"
          @stepChangedAndNext="stepChangedAndNext"
          @nextClicked="nextClicked"
          @prevClicked="prevClicked"
          @getStep="getStep"
        />

        <Transition name="fade-loading">
          <LoaderOverlayComponent
            v-if="updating || loading"
            :loading="updating || loading"
          />
        </Transition>
      </StepCe>
    </template>
  </div>
</template>
<script lang="ts" setup>
import { defineAsyncComponent, Ref, ref, shallowRef, watch } from 'vue';
import { Step } from '@tradein/api';
import StepCe from '../../components/step/step.vue';
import ModalComponentCe from '../../components/modal/modal.vue';
import ConfirmationCe from '../../templates/confirmation/confirmation.vue';
import LoaderOverlayComponent from '../../components/loader-overlay/loader-overlay.vue';
import { Notification, StepIds } from '@tradein/store';
import { trackingService } from '../../utils/tracking';

interface Props {
  step: Step;
  history: Step[];
  loading: boolean;
  updating: boolean;
  notification: Notification;
}

const props = defineProps<Props>();
const emit = defineEmits([
  'stepChanged',
  'stepChangedAndNext',
  'nextClicked',
  'prevClicked',
  'closeNotification',
  'getStep',
  'navigateToStep',
]);

const currentComponent = shallowRef(null);
const currentComponentName = ref('');

const checkIfActive = (id: string) => {
  return props.step.stepId === id;
};

const getComponent = (stepId: string) => {
  switch (stepId) {
    case StepIds.vehicledata:
    case StepIds.vinidentification:
    case StepIds.modelselection:
    case StepIds.mesearch:
    case StepIds.licenseplateidentification:
    case StepIds.medetails:
    case StepIds.enginetransmission:
    case StepIds.modelselect:
    case StepIds.lineselection:
    case StepIds.colorselection:
    case StepIds.cityselection: {
      return defineAsyncComponent(
        () => import('../vehicle-data/vehicle-data.vue'),
      );
    }
    case StepIds.emailrequest:
    case StepIds.dealercontact:
    case StepIds.dealerrequest:
    case StepIds.desiredvehicle: {
      return defineAsyncComponent(
        () => import('../contact-information/contact-information.vue'),
      );
    }
    case StepIds.dealerselection: {
      return defineAsyncComponent(
        () => import('../dealer-selection/dealer-selection.vue'),
      );
    }
    case StepIds.ratelimit:
    case StepIds.emailconfirmation:
    case StepIds.dealerconfirmation:
    case StepIds.valuationrejection:
    case StepIds.emaildealerconfirmation: {
      return defineAsyncComponent(
        () => import('../confirmation/confirmation.vue'),
      );
    }
    default: {
      return defineAsyncComponent(
        () => import('../application-error/application-error.vue'),
      ) as any;
    }
  }
};

const getComponentName = (stepId: string) => {
  const components: Record<string, string> = {
    [StepIds.vehicledata]: 'vehicle-data',
    [StepIds.vinidentification]: 'vehicle-data',
    [StepIds.modelselection]: 'vehicle-data',
    [StepIds.mesearch]: 'vehicle-data',
    [StepIds.licenseplateidentification]: 'vehicle-data',
    [StepIds.medetails]: 'vehicle-data',
    [StepIds.enginetransmission]: 'vehicle-data',
    [StepIds.modelselect]: 'vehicle-data',
    [StepIds.lineselection]: 'vehicle-data',
    [StepIds.colorselection]: 'vehicle-data',
    [StepIds.cityselection]: 'vehicle-data',
    [StepIds.emailrequest]: 'contact-information',
    [StepIds.dealercontact]: 'contact-information',
    [StepIds.dealerrequest]: 'contact-information',
    [StepIds.desiredvehicle]: 'contact-information',
    [StepIds.dealerselection]: 'dealer-selection',
    [StepIds.ratelimit]: 'confirmation',
    [StepIds.emailconfirmation]: 'confirmation',
    [StepIds.dealerconfirmation]: 'confirmation',
    [StepIds.valuationrejection]: 'confirmation',
    [StepIds.emaildealerconfirmation]: 'confirmation',
  };

  return components[stepId];
};

watch(
  () => props.step.stepId,
  () => {
    const prevComponentName = currentComponentName.value;
    currentComponentName.value = getComponentName(props.step.stepId);
    if (currentComponentName.value !== prevComponentName) {
      currentComponent.value = getComponent(props.step.stepId) as any;
    }
  },
  { immediate: true },
);

watch(
  () => props.step.stepId,
  () => {
    if (props.step?.stepId !== StepIds.vehicledata) {
      trackingService.publishClick(`impression`, `step.${props.step?.stepId}`);

      if (
        props.step?.stepId === StepIds.dealerconfirmation ||
        props.step.stepId === StepIds.emaildealerconfirmation
      ) {
        trackingService.publishClick(`form`, `form_submitted`);
      }
    }
  },
);

const checkIfCompleted = (id: string) => {
  return props.history.filter((step) => step.stepId === id).length > 0 || false;
};
const stepChanged = (data: Step): void => {
  emit('stepChanged', data);
};
const stepChangedAndNext = (data: Step): void => {
  emit('stepChangedAndNext', data);
};
const nextClicked = (data: { delay: number }): void => {
  emit('nextClicked', data);
};
const prevClicked = (): void => {
  emit('prevClicked');
};
const closeNotification = (): void => {
  emit('closeNotification');
};
const getStep = (data: string): void => {
  emit('getStep', data);
};
const navigateToStep = (data: string): void => {
  emit('navigateToStep', data);

  trackingService.publishClick(`feature`, `${props.step.stepId}.edit.${data}`);
};
</script>

<style lang="scss">
.ti-stepper {
  position: relative;
  width: 100%;
}
</style>
